import { Banner } from '@/components/DesignSystem';
import { LocalStorageUtility } from '@/utilities/LocalStorageUtility';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { daysFromDate, getJsDateFromString } from '@shared/utilities';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NewProgramTierBenefitsBannerProps {
  program: ApiRewardsProgram,
}

const NewProgramTierBenefitLocalStorageKey = 'new-program-benefit-banner-dismissed';

export const NewProgramTierBenefitsBanner = ({
  program,
}: NewProgramTierBenefitsBannerProps) => {
  const { t } = useTranslation();
  const [shouldShow, setShouldShow] = useState(false);

  const programStartDate = getJsDateFromString(program.startDate);
  const programEndDate = getJsDateFromString(program.endDate, { endOfDay: true });

  useEffect(() => {
    const currentDate = new Date();
    const daysSinceProgramStart = Math.floor(daysFromDate(programStartDate));

    const newProgramStarted = (
      currentDate >= programStartDate
      && currentDate <= programEndDate
      && daysSinceProgramStart <= 30
    );

    if (!newProgramStarted) {
      return;
    }

    const dismissed = LocalStorageUtility.getItem(NewProgramTierBenefitLocalStorageKey);
    if (dismissed) {
      const dismissedDate = getJsDateFromString(dismissed);
      const isStillInProgramPeriod =
        dismissedDate >= programStartDate && dismissedDate <= programEndDate;
      if (isStillInProgramPeriod) {
        return;
      }
    }

    setShouldShow(true);
  }, [program]);

  const handleDismiss = () => {
    LocalStorageUtility.set(NewProgramTierBenefitLocalStorageKey, new Date().toISOString());
    setShouldShow(false);
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <Banner
      onClose={handleDismiss}
      status="info"
      sx={{ p: '8px 16px' }}
      title={t($ => $.customerDashboard.benefits.tierBenefitsChanged)}
      titleTextCategory="body-medium"
    />
  );
};
